/**
 * Workshop: Workflow frame configuration panel.
 *
 * Shows workflow settings + detailed step parameters.
 * Each step expands to show type-specific config:
 *   trigger    → schedule, timezone
 *   agent_task → prompt text
 *   tool_call  → tool name + all parameters (key: value)
 *   approval   → (just status)
 *   conditional → branch labels + targets
 */
import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";
import { Badge } from "@/components/ui/badge";
import type {
  FlowStep,
  FlowStepStatus,
  TriggerType,
  WorkflowRun,
} from "@/workshop/components/shapes/workflow-frame/types";

// ─── Types ──────────────────────────────────────────────────────────────────

type PanelTab = "config" | "runs";

interface WorkflowFramePanelProps {
  name: string;
  triggerType: TriggerType;
  schedule?: string;
  steps: FlowStep[];
  /** Execution history for the Runs tab. */
  runs?: WorkflowRun[];
  onNameChange?: (name: string) => void;
  onTriggerChange?: (trigger: TriggerType) => void;
  onRun?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
  className?: string;
  "data-testid"?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const STATUS_DOT: Record<string, string> = {
  completed: "bg-emerald-400",
  running: "bg-blue-400 animate-pulse",
  failed: "bg-red-400",
  pending: "bg-muted",
  waiting: "bg-amber-400",
};

const STEP_TYPE_LABEL: Record<string, string> = {
  trigger: "Trigger",
  agent_task: "Agent",
  tool_call: "Tool",
  approval_gate: "Approval",
  conditional: "Condition",
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
      {children}
    </span>
  );
}

function ParamRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="shrink-0 text-[9px] text-muted">{label}</span>
      <span className="flex-1 text-right text-[9px] text-secondary break-all">
        {value}
      </span>
    </div>
  );
}

function ToggleButton({ label, isActive, onClick, testId }: {
  label: string; isActive: boolean; onClick: () => void; testId: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex-1 rounded-md py-1 text-center text-[10px] font-medium transition-colors",
        isActive
          ? "bg-glass-fill-heavy text-accent"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-pressed={isActive}
    >
      {label}
    </button>
  );
}

// ─── Step detail ────────────────────────────────────────────────────────────

function StepDetail({ step }: { step: FlowStep }) {
  return (
    <div
      className={cn(
        "rounded-md border border-glass-stroke-subtle px-2.5 py-2",
        step.status === "failed" && "border-red-400/30 bg-red-400/5",
      )}
      data-testid={`workflow-panel-step-${step.id}`}
    >
      {/* Step header */}
      <div className="flex items-center gap-2">
        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", STATUS_DOT[step.status] ?? "bg-muted")} />
        <span className="flex-1 truncate text-[10px] font-medium text-primary">
          {step.title}
        </span>
        <Badge variant="outline" dot={false} className="text-[8px]">
          {STEP_TYPE_LABEL[step.type] ?? step.type}
        </Badge>
      </div>

      {/* Type-specific parameters */}
      <div className="mt-1.5 flex flex-col gap-1 pl-3.5">
        {step.type === "trigger" && step.triggerConfig && (
          <>
            <ParamRow label="type" value={step.triggerConfig.type} />
            {step.triggerConfig.schedule && <ParamRow label="schedule" value={step.triggerConfig.schedule} />}
            {step.triggerConfig.timezone && <ParamRow label="timezone" value={step.triggerConfig.timezone} />}
            {step.triggerConfig.eventDescription && <ParamRow label="event" value={step.triggerConfig.eventDescription} />}
          </>
        )}

        {step.type === "agent_task" && step.prompt && (
          <p className="text-[9px] leading-tight text-secondary line-clamp-3">
            {step.prompt}
          </p>
        )}

        {step.type === "tool_call" && (
          <>
            {step.toolName && <ParamRow label="tool" value={step.toolName} />}
            {step.toolParams && Object.entries(step.toolParams).map(([key, val]) => (
              <ParamRow key={key} label={key} value={val} />
            ))}
          </>
        )}

        {step.type === "conditional" && step.branches && (
          <>
            {step.branches.map((b) => (
              <ParamRow key={b.label} label={b.label} value={`→ ${b.targetStepId}`} />
            ))}
          </>
        )}

        {step.durationMs != null && (
          <ParamRow label="duration" value={`${(step.durationMs / 1000).toFixed(1)}s`} />
        )}

        {step.status === "failed" && step.output && (
          <p className="mt-0.5 text-[9px] leading-tight text-red-300">
            {step.output}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Tab button ─────────────────────────────────────────────────────────────

function PanelTabButton({
  label,
  isActive,
  onClick,
  testId,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex-1 py-2 text-center text-[10px] font-medium transition-colors",
        isActive
          ? "border-b-2 border-accent text-primary"
          : "text-muted hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-selected={isActive}
      role="tab"
    >
      {label}
    </button>
  );
}

// ─── Runs tab ───────────────────────────────────────────────────────────────

const RUN_STATUS_COLORS: Record<FlowStepStatus, string> = {
  pending: "bg-muted",
  running: "bg-blue-400 shadow-[0_0_4px_rgba(96,165,250,0.6)] animate-pulse",
  completed: "bg-emerald-400",
  failed: "bg-red-400",
  waiting: "bg-amber-400",
};

function formatRunDate(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  const h12 = hours % 12 || 12;
  return `${month} ${day}, ${h12}:${minutes}${ampm}`;
}

function formatRunDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function RunsTabContent({ runs }: { runs: WorkflowRun[] }) {
  if (runs.length === 0) {
    return (
      <p className="px-3.5 py-6 text-center text-[10px] text-muted">No runs yet</p>
    );
  }

  return (
    <div className="flex flex-col" data-testid="workflow-panel-runs-content">
      {runs.map((run) => (
        <div
          key={run.id}
          className="flex items-center gap-2.5 border-b border-glass-stroke-subtle px-3.5 py-2.5 transition-colors hover:bg-glass-fill-heavy/30"
          data-testid={`workflow-panel-run-${run.id}`}
        >
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full",
              RUN_STATUS_COLORS[run.status],
            )}
            aria-hidden="true"
          />
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-[10px] font-medium text-secondary">
              {formatRunDate(run.startedAt)}
            </span>
            {run.durationMs != null && (
              <span className="text-[9px] text-muted">
                {formatRunDuration(run.durationMs)}
              </span>
            )}
          </div>
          <Badge
            variant={
              run.status === "completed"
                ? "default"
                : run.status === "failed"
                  ? "error"
                  : run.status === "running"
                    ? "selected"
                    : "outline"
            }
            dot={false}
            className="text-[8px]"
            data-testid={`workflow-panel-run-${run.id}-badge`}
          >
            {run.status === "completed"
              ? "Success"
              : run.status === "failed"
                ? "Failed"
                : run.status === "running"
                  ? "Running"
                  : "Pending"}
          </Badge>
        </div>
      ))}
    </div>
  );
}

// ─── Config tab ─────────────────────────────────────────────────────────────

function ConfigTabContent({
  name,
  triggerType,
  schedule,
  steps,
  onNameChange,
  onTriggerChange,
  onRun,
  onDelete,
}: {
  name: string;
  triggerType: TriggerType;
  schedule?: string;
  steps: FlowStep[];
  onNameChange?: (name: string) => void;
  onTriggerChange?: (trigger: TriggerType) => void;
  onRun?: () => void;
  onDelete?: () => void;
}) {
  return (
    <>
      {/* Name */}
      <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
        <SectionLabel>Name</SectionLabel>
        <TextInput
          value={name}
          onChange={(e) => onNameChange?.(e.target.value)}
          placeholder="Workflow name"
          data-testid="workflow-panel-name-input"
        />
      </div>

      {/* Trigger */}
      <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
        <SectionLabel>Trigger</SectionLabel>
        <div className="flex gap-1 rounded-lg bg-glass-fill-heavy p-0.5" role="group">
          <ToggleButton label="Manual" isActive={triggerType === "manual"} onClick={() => onTriggerChange?.("manual")} testId="workflow-panel-trigger-manual" />
          <ToggleButton label="Schedule" isActive={triggerType === "cron"} onClick={() => onTriggerChange?.("cron")} testId="workflow-panel-trigger-cron" />
          <ToggleButton label="Event" isActive={triggerType === "event"} onClick={() => onTriggerChange?.("event")} testId="workflow-panel-trigger-event" />
        </div>
        {triggerType === "cron" && (
          <TextInput value={schedule ?? ""} placeholder="0 9 * * MON-FRI" readOnly data-testid="workflow-panel-schedule-input" className="mt-1" />
        )}
      </div>

      {/* Steps with full parameters */}
      <div className="flex flex-col gap-1.5 border-b border-glass-stroke-subtle px-3.5 py-3">
        <SectionLabel>Steps</SectionLabel>
        {steps.length === 0 ? (
          <p className="text-center text-[10px] text-muted">No steps defined</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {steps.map((step) => (
              <StepDetail key={step.id} step={step} />
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 px-3.5 py-3">
        <Button variant="default" size="sm" onClick={onRun} data-testid="workflow-panel-run" className="w-full">
          <Icons.Play className="h-3 w-3" />
          Run Workflow
        </Button>
        <Button variant="destructive" size="sm" onClick={onDelete} data-testid="workflow-panel-delete" className="w-full">
          <Icons.Trash2 className="h-3 w-3" />
          Delete
        </Button>
      </div>
    </>
  );
}

// ─── Main panel ─────────────────────────────────────────────────────────────

export function WorkflowFramePanel({
  name, triggerType, schedule, steps, runs = [],
  onNameChange, onTriggerChange, onRun, onDelete, onClose,
  className, "data-testid": testId = "workflow-frame-panel",
}: WorkflowFramePanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>("config");
  const completed = steps.filter((s) => s.status === "completed").length;
  const total = steps.length;

  return (
    <aside
      className={cn(
        "flex w-64 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Header */}
      <div className="flex h-11 items-center gap-2 border-b border-glass-stroke-subtle px-3.5">
        <Icons.Zap className="h-3.5 w-3.5 shrink-0 text-accent" />
        <span className="text-xs font-semibold text-primary">Workflow</span>
        <Badge variant="outline" dot={false} className="text-[8px]">{completed}/{total}</Badge>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-md p-0.5 text-muted transition-colors hover:text-primary"
          data-testid="workflow-panel-close"
          aria-label="Close panel"
        >
          <Icons.X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Tab bar */}
      <div
        className="flex border-b border-glass-stroke-subtle"
        role="tablist"
        aria-label="Workflow panel tabs"
      >
        <PanelTabButton
          label="Config"
          isActive={activeTab === "config"}
          onClick={() => setActiveTab("config")}
          testId="workflow-panel-tab-config"
        />
        <PanelTabButton
          label={`Runs${runs.length > 0 ? ` (${runs.length})` : ""}`}
          isActive={activeTab === "runs"}
          onClick={() => setActiveTab("runs")}
          testId="workflow-panel-tab-runs"
        />
      </div>

      {/* Scrollable content */}
      <div className="scrollbar-none flex-1 overflow-y-auto">
        {activeTab === "config" && (
          <ConfigTabContent
            name={name}
            triggerType={triggerType}
            schedule={schedule}
            steps={steps}
            onNameChange={onNameChange}
            onTriggerChange={onTriggerChange}
            onRun={onRun}
            onDelete={onDelete}
          />
        )}
        {activeTab === "runs" && <RunsTabContent runs={runs} />}
      </div>
    </aside>
  );
}

export type { WorkflowFramePanelProps };
